import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingContactIcons from '../../components/feature/FloatingContactIcons';
import { api } from '../../lib/api';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  status: 'draft' | 'published';
  featured_image: string;
  tags: string[];
  reading_time: number;
  published_at: string | null;
  created_at: string;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/blog?status=published')
      .then(data => setPosts(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* Hero */}
      <section className="relative py-32 bg-[#1a1a22] overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #C5A46D 0%, transparent 60%), radial-gradient(circle at 70% 50%, #C5A46D 0%, transparent 60%)' }}
        />
        <div className="container mx-auto px-6 text-center relative z-10">
          <p className="text-[#C5A46D] tracking-[0.3em] text-sm font-medium mb-4 uppercase">Golden Tower</p>
          <h1 className="text-5xl md:text-7xl font-light tracking-wide mb-6">
            Our <span className="font-bold text-[#C5A46D]">Blog</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-lg">
            Insights, news and stories from the world of Golden Tower
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-10 h-10 border-2 border-[#C5A46D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-24 text-gray-500">
              <i className="ri-article-line text-5xl block mb-4 opacity-30"></i>
              <p className="text-lg">No blog posts published yet.</p>
            </div>
          ) : (
            <>
              {/* Featured first post */}
              {posts[0] && (
                <div
                  className="mb-16 cursor-pointer group"
                  onClick={() => navigate(`/blog/${posts[0].slug}`)}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden bg-[#111118] border border-white/5 hover:border-[#C5A46D]/30 transition-all duration-500">
                    <div className="relative h-72 lg:h-auto overflow-hidden">
                      {posts[0].featured_image ? (
                        <img
                          src={posts[0].featured_image}
                          alt={posts[0].title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#C5A46D]/20 to-[#1a1a22] flex items-center justify-center">
                          <i className="ri-image-line text-5xl text-[#C5A46D]/30"></i>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="bg-[#C5A46D] text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Featured</span>
                        {posts[0].reading_time && (
                          <span className="text-gray-500 text-sm flex items-center gap-1">
                            <i className="ri-time-line"></i> {posts[0].reading_time} min read
                          </span>
                        )}
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-light mb-4 group-hover:text-[#C5A46D] transition-colors duration-300 leading-snug">
                        {posts[0].title}
                      </h2>
                      {posts[0].excerpt && (
                        <p className="text-gray-400 mb-6 line-clamp-3 leading-relaxed">{posts[0].excerpt}</p>
                      )}
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <i className="ri-user-line text-[#C5A46D]"></i>
                          <span>{posts[0].author}</span>
                          <span>•</span>
                          <span>{formatDate(posts[0].published_at || posts[0].created_at)}</span>
                        </div>
                        <span className="text-[#C5A46D] text-sm flex items-center gap-1 group-hover:gap-2 transition-all duration-300">
                          Read More <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                      {posts[0].tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-5">
                          {posts[0].tags.slice(0, 4).map(tag => (
                            <span key={tag} className="px-3 py-1 bg-white/5 text-gray-400 rounded-full text-xs">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Remaining posts grid */}
              {posts.length > 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.slice(1).map(post => (
                    <article
                      key={post.id}
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="group cursor-pointer bg-[#111118] border border-white/5 rounded-xl overflow-hidden hover:border-[#C5A46D]/30 hover:-translate-y-1 transition-all duration-400"
                    >
                      {/* Thumbnail */}
                      <div className="relative h-48 overflow-hidden">
                        {post.featured_image ? (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#C5A46D]/10 to-[#1a1a22] flex items-center justify-center">
                            <i className="ri-image-line text-3xl text-[#C5A46D]/20"></i>
                          </div>
                        )}
                      </div>
                      {/* Content */}
                      <div className="p-6">
                        {post.reading_time && (
                          <p className="text-gray-500 text-xs flex items-center gap-1 mb-3">
                            <i className="ri-time-line text-[#C5A46D]"></i> {post.reading_time} min read
                          </p>
                        )}
                        <h3 className="font-medium text-lg mb-3 group-hover:text-[#C5A46D] transition-colors duration-300 line-clamp-2 leading-snug">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="text-gray-400 text-sm line-clamp-2 mb-4 leading-relaxed">{post.excerpt}</p>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-white/5">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line text-[#C5A46D]"></i> {post.author}
                          </span>
                          <span>{formatDate(post.published_at || post.created_at)}</span>
                        </div>
                        {post.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {post.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-2 py-0.5 bg-white/5 text-gray-500 rounded text-xs">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      <FloatingContactIcons />
    </div>
  );
};

export default BlogPage;
